import { useMemo, useEffect, useState } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { matchPath } from "react-router";
import { getAIChats, getAIChatById } from "../queries/ai.graphql";
import { useWorkspaceFeatureFlags } from "../contexts/global/WorkspaceContext";
import { DASHBOARD_REGEX } from "../components/AIContent/consts";

export const getDashboardsFromChat = (chat) => {
  const dashboardMap = {};

  if (!chat) {
    return dashboardMap;
  }

  if (!chat.messages || chat.messages.length === 0) {
    return dashboardMap;
  }

  chat.messages.forEach((message, messageIndex) => {
    if (message.role !== "assistant" || !message.content) {
      return;
    }

    // Match dashboard patterns with content: |dashboard title="Dashboard Name"|</p>content|dashboard|
    // Also handles cases where closing |dashboard| tag is missing (assumes rest of content is dashboard)
    const dashboardRegex = DASHBOARD_REGEX;
    const matches = [...message.content.matchAll(dashboardRegex)];

    // Process matches in reverse order (last dashboard in message wins for same chat)
    // This ensures if AI regenerates a dashboard in the same message, we get the latest
    matches.reverse().forEach((match) => {
      const attributesString = match[1];
      const content = match[2];

      // Extract title attribute
      const titleMatch = attributesString.match(/title=["']([^"']*)["']/);
      const rawTitle = titleMatch ? titleMatch[1] : null;

      if (rawTitle && content) {
        // Decode HTML entities (e.g., &amp; -> &)
        const tempElement = document.createElement("textarea");
        tempElement.innerHTML = rawTitle;
        const title = tempElement.value;

        // Use the chat id and title as the key to deduplicate dashboards with same name
        const key = `${chat.id}__${title}`;

        const maybeExisting = dashboardMap[key];
        // If the dashboard is already in the map, replace it if the new message is newer
        if (
          maybeExisting
            ? new Date(maybeExisting.createdAt) < new Date(message.createdAt)
            : true
        ) {
          dashboardMap[key] = {
            title,
            chatId: chat.id,
            chatTitle: chat.title,
            messageId: message.id,
            messageIndex,
            content: content.trim(),
            createdAt: message.createdAt,
          };
        }
      }
    });
  });

  return dashboardMap;
};

/**
 * Hook to extract all AI-created dashboards from chat history
 * @returns {Object} { dashboards: Array, loading: boolean, error: Error, selectedDashboardId: string }
 */
const useAIDashboards = () => {
  const { enableAIDashboards } = useWorkspaceFeatureFlags();
  const [allChatsData, setAllChatsData] = useState([]);
  const [fetchedCount, setFetchedCount] = useState(0);
  const location = useLocation();

  // First, get all chat IDs
  const { data: chatsListData, loading: chatsListLoading } = useQuery(
    getAIChats,
    {
      fetchPolicy: "cache-and-network",
      skip: !enableAIDashboards,
    }
  );

  // Lazy query to fetch individual chats with messages
  const [fetchChatById, { loading: chatByIdLoading }] =
    useLazyQuery(getAIChatById);

  // Fetch all chats with messages when chat list is ready
  useEffect(() => {
    const fetchAllChats = async () => {
      if (
        !enableAIDashboards ||
        !chatsListData?.chats ||
        chatsListData.chats.length === 0
      ) {
        return;
      }

      const chatIds = chatsListData.chats.map((chat) => chat.id);
      const results = [];

      // Fetch each chat individually
      for (const chatId of chatIds) {
        try {
          const { data } = await fetchChatById({
            variables: { id: chatId },
          });
          if (data?.chat) {
            results.push(data.chat);
          }
          setFetchedCount((prev) => prev + 1);
        } catch (err) {
          // Silently skip failed chats
        }
      }

      setAllChatsData(results);
    };

    fetchAllChats();
  }, [enableAIDashboards, chatsListData, fetchChatById]);

  const dashboards = useMemo(() => {
    if (allChatsData.length === 0) {
      return [];
    }

    const dashboardMap = allChatsData.reduce((acc, chat) => {
      return { ...acc, ...getDashboardsFromChat(chat) };
    }, {});

    // Convert map to array and sort by creation date (most recent first)
    return Object.values(dashboardMap)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .map(({ messageIndex, ...dashboard }) => dashboard); // Remove internal fields from final output
  }, [allChatsData]);

  const loading =
    chatsListLoading ||
    chatByIdLoading ||
    (chatsListData?.chats && allChatsData.length < chatsListData.chats.length);

  // Detect the currently active dashboard based on URL
  const selectedDashboardId = useMemo(() => {
    const match = matchPath(location.pathname, {
      path: "/:workspaceId/dashboards/:chatId/:dashboardId",
      exact: true,
    });

    if (match?.params?.chatId && match?.params?.dashboardId) {
      const decodedTitle = decodeURIComponent(match.params.dashboardId);
      const dashboard = dashboards.find(
        (d) => d.chatId === match.params.chatId && d.title === decodedTitle
      );
      return `${dashboard?.chatId}__${dashboard?.title}`;
    }

    return null;
  }, [location.pathname, dashboards]);

  return {
    dashboards,
    loading,
    error: null,
    selectedDashboardId,
    progress:
      chatsListData?.chats?.length > 0
        ? `${fetchedCount}/${chatsListData.chats.length}`
        : null,
  };
};

export default useAIDashboards;
